import { Department } from '@admin/manage-customer/models/user';
import { DepartmentResponse } from '@app/models/department';
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  CaseReducer,
} from '@reduxjs/toolkit';
import { DepartmentNode, ModifyDepartment } from '../models/department';
import { departmentServices } from '../services/department';
import { handleErrorResponse } from '@app/utils/helpers';
import { RequestLoading } from '@app/utils/constants';

const requests = ['createDepartment', 'updateDepartment'] as const;

type DepartmentRequest = RequestLoading<(typeof requests)[number]>;
// SetupState
interface State {
  loading: boolean;
  dataTable: DepartmentResponse;
  getDepartmentLoading: boolean;
  departmentList: DepartmentNode[];
  selectedDepartment: DepartmentNode | undefined;
  departmentRequest: DepartmentRequest;
}

const initialState: State = {
  loading: false,
  dataTable: {
    data: [],
    paging: undefined,
  },
  departmentList: [],
  getDepartmentLoading: false,
  selectedDepartment: undefined,
  departmentRequest: {
    loading: false,
  },
};

type CR<T> = CaseReducer<State, PayloadAction<T>>;
const TYPE_PREFIX = 'admin/management-department/department/';

// AsyncThunk
const getDepartment = createAsyncThunk(
  TYPE_PREFIX + 'getDepartments',
  async (arg: {
    index: number;
    size: number;
    code?: string;
    name?: string;
  }) => {
    const result = await departmentServices.getDepartment(
      arg.index,
      arg.size,
      arg.code!,
      arg.name!,
    );
    return result;
  },
);

const getSystemDepartmentList = createAsyncThunk(
  TYPE_PREFIX + 'getSystemDepartmentList',
  async () => {
    const result = await departmentServices.getAllDepartments();
    return result;
  },
);

const getAllSystemDepartmentListForAdmin = createAsyncThunk(
  TYPE_PREFIX + 'getAllSystemDepartmentListForAdmin',
  async () => {
    const result = await departmentServices.getAllDepartmentsForAdmin();
    return result;
  },
);

const getDepartmentDetail = createAsyncThunk(
  TYPE_PREFIX + 'getDepartmentDetail',
  async (id: number) => {
    const result = await departmentServices.getDepartmentDetail(id);
    return result;
  },
);

const createNewDepartment = createAsyncThunk<void, ModifyDepartment>(
  TYPE_PREFIX + 'createNewDepartment',
  async (data, { rejectWithValue }) => {
    try {
      return await departmentServices.createDepartment(data);
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateDepartment = createAsyncThunk<void, ModifyDepartment>(
  TYPE_PREFIX + 'updateDepartment',
  async (data, { rejectWithValue }) => {
    try {
      return await departmentServices.updateDepartment(data);
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const slice = createSlice({
  name: 'department',
  initialState,
  reducers: {
    setSelectedDepartment: (state, action) => {
      // state.selectedDepartment = action.payload;
    },
  },
  extraReducers: (builder) => {
    //getModule
    builder.addCase(getDepartment.pending, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));
    builder.addCase(getDepartment.fulfilled, (state, { payload }) => ({
      ...state,
      dataTable: payload,
      getDepartmentLoading: false,
    }));
    builder.addCase(getDepartment.rejected, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));

    //getSystemModuleList
    builder.addCase(getSystemDepartmentList.pending, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));
    builder.addCase(
      getSystemDepartmentList.fulfilled,
      (state, { payload }) => ({
        ...state,
        departmentList: payload,
        getDepartmentLoading: false,
      }),
    );
    builder.addCase(getSystemDepartmentList.rejected, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));

    //get all department for admin role

    builder.addCase(getAllSystemDepartmentListForAdmin.pending, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));
    builder.addCase(
      getAllSystemDepartmentListForAdmin.fulfilled,
      (state, { payload }) => ({
        ...state,
        departmentList: payload,
        getDepartmentLoading: false,
      }),
    );
    builder.addCase(getAllSystemDepartmentListForAdmin.rejected, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));

    //getModuleDetail
    builder.addCase(getDepartmentDetail.pending, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));
    builder.addCase(getDepartmentDetail.fulfilled, (state, { payload }) => ({
      ...state,
      selectedDepartment: payload,
      getDepartmentLoading: false,
    }));
    builder.addCase(getDepartmentDetail.rejected, (state) => ({
      ...state,
      getDepartmentLoading: true,
    }));

    // create new department
    builder.addCase(createNewDepartment.pending, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: true,
        name: 'createDepartment',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewDepartment.fulfilled, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: false,
        name: 'createDepartment',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewDepartment.rejected, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: false,
        name: 'createDepartment',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // update existed department
    builder.addCase(updateDepartment.pending, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: true,
        name: 'updateDepartment',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateDepartment.fulfilled, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: false,
        name: 'updateDepartment',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateDepartment.rejected, (state, action) => ({
      ...state,
      departmentRequest: {
        loading: false,
        name: 'updateDepartment',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));
  },
});

export const { setSelectedDepartment } = slice.actions;
export {
  getDepartment,
  getSystemDepartmentList,
  getDepartmentDetail,
  getAllSystemDepartmentListForAdmin,
  createNewDepartment,
  updateDepartment,
};
export default slice.reducer;
